import React, { useState } from 'react';
import { BookOpen, CheckCircle, ExternalLink, Clock, Paperclip, Eye, FileText } from 'lucide-react';
import FileViewer from './FileViewer';

const TrainingViewer = ({ content, onComplete, isCompleted, lastSubmissionCancelled, previousResponse, isAdmin = false, children }) => {
    // Note: TrainingViewer doesn't have a rigid 'submitted' state that locks UI like QuizViewer,
    // but we accept the prop for consistency and future-proofing.

    const [training, setTraining] = useState(() => {
        try {
            if (typeof content === 'object' && content !== null) {
                return content;
            }
            return JSON.parse(content);
        } catch (e) {
            console.error("Error parsing training content:", e);
            return { sections: [] };
        }
    });

    const [completedSections, setCompletedSections] = useState(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                return new Set(parsed.completedSections || []);
            } catch (e) {
                return new Set();
            }
        }
        return new Set();
    });

    const [activeSection, setActiveSection] = useState(0);

    // Sync state with previousResponse prop changes
    React.useEffect(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                setCompletedSections(new Set(parsed.completedSections || []));
            } catch (e) {
                setCompletedSections(new Set());
            }
        } else {
            // Reset for retake
            setCompletedSections(new Set());
            setActiveSection(0);
        }
    }, [previousResponse]);
    const [previewFile, setPreviewFile] = useState(null);

    const handleSectionComplete = (sectionIndex) => {
        setCompletedSections(prev => new Set([...prev, sectionIndex]));
    };

    const handleCompleteTraining = () => {
        const responseData = {
            completedSections: Array.from(completedSections),
            totalSections: training.sections.length,
            completedAt: new Date().toISOString()
        };

        onComplete(JSON.stringify(responseData));
    };

    const allSectionsComplete = completedSections.size === training.sections.length;

    if (!training.sections || training.sections.length === 0) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <BookOpen className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <p className="text-yellow-800">No training content available</p>
            </div>
        );
    }

    return (
        <div className={isAdmin ? "space-y-4" : "space-y-6"}>
            {/* Training Header - Compact for Admin */}
            <div className={`bg - blue - 50 border border - blue - 200 rounded - lg ${isAdmin ? 'p-3' : 'p-4'} `}>
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <BookOpen className={`text - blue - 600 ${isAdmin ? 'w-4 h-4' : 'w-5 h-5'} `} />
                            <h3 className={`font - semibold text - blue - 900 ${isAdmin ? 'text-sm' : ''} `}>Training Material</h3>
                        </div>
                        <div className="flex gap-4 text-sm text-blue-700">
                            <span>{training.sections.length} Sections</span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {training.sections.reduce((sum, s) => sum + (s.duration || 0), 0)} min
                            </span>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-gray-600">Progress</p>
                        <p className={`font - bold text - blue - 700 ${isAdmin ? 'text-lg' : 'text-2xl'} `}>
                            {completedSections.size}/{training.sections.length}
                        </p>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-3">
                    <div className="w-full bg-blue-200 rounded-full h-2">
                        <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{
                                width: `${(completedSections.size / training.sections.length) * 100}% `
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Section Navigation */}
            <div className={`bg - white border border - gray - 200 rounded - lg ${isAdmin ? 'p-3' : 'p-4'} `}>
                <h4 className={`font - semibold text - gray - 800 mb - 3 ${isAdmin ? 'text-sm' : ''} `}>Sections</h4>
                <div className={isAdmin ? "space-y-1" : "space-y-2"}>
                    {training.sections.map((section, index) => (
                        <button
                            key={index}
                            onClick={() => setActiveSection(index)}
                            className={`w - full flex items - center gap - 3 rounded - lg transition - all ${isAdmin ? 'p-2' : 'p-3'} ${activeSection === index
                                    ? 'bg-blue-50 border-2 border-blue-500'
                                    : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                                } `}
                        >
                            <div className={`flex items - center justify - center rounded - full ${isAdmin ? 'w-6 h-6 text-xs' : 'w-8 h-8'} ${completedSections.has(index)
                                    ? 'bg-green-500 text-white'
                                    : activeSection === index
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-300 text-gray-600'
                                } `}>
                                {completedSections.has(index) ? (
                                    <CheckCircle className={isAdmin ? "w-4 h-4" : "w-5 h-5"} />
                                ) : (
                                    <span className="font-semibold">{index + 1}</span>
                                )}
                            </div>
                            <div className="flex-1 text-left">
                                <p className={`font - medium text - gray - 900 ${isAdmin ? 'text-sm' : ''} `}>{section.title || `Section ${index + 1} `}</p>
                                <p className="text-xs text-gray-500">{section.duration || 10} min</p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Active Section Content */}
            {training.sections[activeSection] && (
                <div className={`bg - white border border - gray - 200 rounded - lg ${isAdmin ? 'p-4' : 'p-6'} `}>
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <h2 className={`font - bold text - gray - 900 mb - 2 ${isAdmin ? 'text-lg' : 'text-2xl'} `}>
                                {training.sections[activeSection].title || `Section ${activeSection + 1} `}
                            </h2>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Clock className="w-4 h-4" />
                                <span>{training.sections[activeSection].duration || 10} minutes</span>
                            </div>
                        </div>
                        {!completedSections.has(activeSection) && !isCompleted && !isAdmin && (
                            <button
                                onClick={() => handleSectionComplete(activeSection)}
                                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                            >
                                <CheckCircle className="w-4 h-4" />
                                Mark Complete
                            </button>
                        )}
                    </div>

                    {/* Content */}
                    <div className={`prose max - w - none ${isAdmin ? 'prose-sm' : ''} `}>
                        <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                            {training.sections[activeSection].content}
                        </div>
                    </div>

                    {/* Resources */}
                    {training.sections[activeSection].resources && training.sections[activeSection].resources.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h3 className={`font - semibold text - gray - 900 mb - 3 ${isAdmin ? 'text-sm' : ''} `}>Additional Resources</h3>
                            <div className="space-y-2">
                                {training.sections[activeSection].resources.map((resource, rIndex) => (
                                    <a
                                        key={rIndex}
                                        href={resource.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex items - center gap - 2 bg - gray - 50 hover: bg - gray - 100 rounded - lg transition - colors ${isAdmin ? 'p-2 text-sm' : 'p-3'} `}
                                    >
                                        <ExternalLink className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                        <span className="text-gray-900">{resource.title || resource.url}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Section Attachments */}
                    {training.sections[activeSection].attachments && training.sections[activeSection].attachments.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h3 className={`font - semibold text - gray - 900 mb - 3 flex items - center gap - 2 ${isAdmin ? 'text-sm' : ''} `}>
                                <Paperclip className="w-4 h-4" />
                                Attachments
                            </h3>
                            <div className="space-y-2">
                                {training.sections[activeSection].attachments.map((file, aIndex) => (
                                    <button
                                        key={aIndex}
                                        onClick={() => setPreviewFile(file)}
                                        className={`w - full text - left flex items - center gap - 3 bg - blue - 50 hover: bg - blue - 100 rounded - lg group transition - colors ${isAdmin ? 'p-2' : 'p-3'} `}
                                    >
                                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                                            <FileText className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`font - medium text - gray - 800 truncate ${isAdmin ? 'text-xs' : 'text-sm'} `}>
                                                {file.file_name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                Click to preview
                                            </p>
                                        </div>
                                        <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Navigation */}
                    <div className="mt-6 flex justify-between">
                        <button
                            onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                            disabled={activeSection === 0}
                            className={`px - 4 py - 2 border border - gray - 300 rounded - lg hover: bg - gray - 50 disabled: opacity - 50 disabled: cursor - not - allowed ${isAdmin ? 'text-sm py-1' : ''} `}
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setActiveSection(Math.min(training.sections.length - 1, activeSection + 1))}
                            disabled={activeSection === training.sections.length - 1}
                            className={`px - 4 py - 2 bg - blue - 600 text - white rounded - lg hover: bg - blue - 700 disabled: opacity - 50 disabled: cursor - not - allowed ${isAdmin ? 'text-sm py-1' : ''} `}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Inject Children (Proof/Signature) before Complete Button */}
            {!isCompleted && !isAdmin && children}

            {/* Complete Training Button - Hide for Admin */}
            {allSectionsComplete && !isCompleted && !isAdmin && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-green-900 mb-2">All Sections Completed!</h3>
                    <p className="text-green-700 mb-4">You've finished all training sections. Click below to mark this training as complete.</p>
                    <button
                        onClick={handleCompleteTraining}
                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                        Complete Training
                    </button>
                </div>
            )}

            {/* File Viewer Modal */}
            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />
        </div>
    );
};

export default TrainingViewer;

